import React, { useRef, useEffect } from "react";
import { Box, Button, Container, Typography } from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import { motion } from "framer-motion";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

interface AnimatedHeroProps {
  videoSrc: string;
  lines: string[];
  page?: String;
  data?: Object;
  img?: Boolean;
}

const HeroSection = styled(Box)(({ theme }) => ({
  position: "relative",
  height: "calc(100vh)",
  display: "flex",
  alignItems: "center",
  overflow: "hidden",
  fontSize: "0.5rem",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0, 0, 0, 0.4)",
    zIndex: 1,
  },
}));

const VideoBackground = styled("video")({
  position: "absolute",
  width: "100%",
  height: "100%",
  objectFit: "cover",
  zIndex: 0,
});

const HeroContent = styled(Box)(({ theme }) => ({
  position: "relative",
  zIndex: 2,
  color: "white",
  padding: theme.spacing(4),
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(2),
  },
}));

const LearnMoreButton = styled(Button)(({ theme }) => ({
  backgroundColor: "#AB052D",
  color: "white",
  borderRadius: "50%",
  minWidth: "16px",
  height: "26px",
  padding: 0,
  marginTop: theme.spacing(1),
  transition: "background-color 0.3s, color 0.3s",
  "&:hover": {
    backgroundColor: "white",
    "& .MuiSvgIcon-root": {
      color: "#AB052D",
      transform: "scale(1.2)",
      transition: "transform 0.3s",
    },
  },
}));

const HeroTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,

  marginBottom: theme.spacing(0.5),
  [theme.breakpoints.down("md")]: {
    fontSize: "2.5rem",
    width: "43%",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "2rem",
    width: "93%",
  },
}));

const AnimatedHero: React.FC<AnimatedHeroProps> = ({
  videoSrc,
  lines,
  page = "none",
  data = null,
  img = false,
}) => {
  const theme = useTheme();
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    // Ensure video plays automatically and loops
    if (videoRef.current) {
      videoRef.current.play().catch((error) => {
        console.error("Video play failed:", error);
      });
    }
  }, []);

  const scrollDown = () => {
    window.scrollBy({ top: 600, left: 0, behavior: "smooth" });
  };

  return (
    <HeroSection>
      {img ? (
        <img
          className="w-full h-full absolute object-cover z-0"
          src={videoSrc}
          alt=""
        />
      ) : (
        <VideoBackground ref={videoRef} autoPlay muted loop playsInline>
          <source src={videoSrc} type="video/mp4" />
          Your browser does not support the video tag.
        </VideoBackground>
      )}

      <Container maxWidth="lg">
        <HeroContent>
          {page == "parker" && data != null && <img src={data?.toString()} />}
          {lines.map((line, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 80 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.3 }}
            >
              <HeroTitle variant="h3">{line}</HeroTitle>
            </motion.div>
          ))}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: lines.length * 0.3 }}
            className="flex justify-start gap-2 items-center"
          >
            <LearnMoreButton
              onClick={scrollDown}
              aria-label="Learn More"
              sx={{ width: 40, height: 40 }}
            >
              <ChevronRightIcon />
            </LearnMoreButton>
            <h3 style={{ fontWeight: 300 }} className="  text-lg  pt-3">
              Learn More
            </h3>
          </motion.div>
        </HeroContent>
      </Container>
    </HeroSection>
  );
};

export default AnimatedHero;
